import { html, css, nothing } from 'lit';
import { customElement, property, query, state as litState } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import { buttonStyles } from '../styles/button-styles.js';
import { formStyles } from '../styles/form-styles.js';
import '../ui/modal-dialog.js';
import type { ModalDialog } from '../ui/modal-dialog.js';

interface SliceData {
    id: string;
    name: string;
    artist?: string | null;
    difficulty?: string | null;
    tags?: string[] | null;
    musicxmlPath?: string | null;
    metadata?: { musicxml?: { originalName: string } };
    soundsliceScorehash?: string | null;
}

interface RecordingInfo {
    id: number;
    recordingType: string;
    recordingName: string | null;
    recordingUrl: string | null;
    syncpointCount: number;
    isPrimary: boolean;
}

type CreateSource = 'upload' | 'soundslice';

/**
 * Slice create/edit form modal.
 * Wraps <modal-dialog> with the full slice form, validation, and API calls.
 * Supports both file upload and Soundslice import modes.
 *
 * @fires slice-saved - Fired after successful create or update (detail: { mode })
 * @fires slice-conflict - Fired on 409 conflict (detail: { conflictData, pendingFormData })
 */
@customElement('slice-form-modal')
export class SliceFormModal extends BaseElement {
    static styles = [
        BaseElement.styles,
        buttonStyles,
        formStyles,
        css`
            .form-group select {
                appearance: none;
                cursor: pointer;
                background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23cbd5e1' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
                background-repeat: no-repeat;
                background-position: right 1rem center;
                padding-right: 2.5rem;
            }

            .source-toggle {
                display: flex;
                gap: 0;
                margin-bottom: 1.25rem;
                border: 1px solid var(--border);
                border-radius: 0.5rem;
                overflow: hidden;
            }

            .source-toggle button {
                flex: 1;
                padding: 0.625rem 1rem;
                border: none;
                background: var(--surface);
                color: var(--text-secondary);
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s;
            }

            .source-toggle button.active {
                background: var(--primary-color);
                color: white;
            }

            .source-toggle button:hover:not(.active) {
                background: var(--surface-light);
            }

            .import-hint {
                font-size: 0.8rem;
                color: var(--text-secondary);
                margin-top: 0.25rem;
                line-height: 1.4;
            }

            .loading-message {
                text-align: center;
                padding: 1rem;
                color: var(--text-secondary);
                font-size: 0.9rem;
            }

            .recordings-section {
                margin-top: 1.25rem;
                padding-top: 1.25rem;
                border-top: 1px solid var(--border);
            }

            .recordings-section h4 {
                margin: 0 0 0.75rem 0;
                font-size: 0.9rem;
                color: var(--text-primary);
            }

            .recording-item {
                display: flex;
                align-items: center;
                gap: 0.5rem;
                padding: 0.5rem 0.75rem;
                background: var(--surface);
                border-radius: 0.375rem;
                margin-bottom: 0.5rem;
                font-size: 0.85rem;
            }

            .recording-badge {
                display: inline-block;
                padding: 0.125rem 0.5rem;
                border-radius: 0.25rem;
                font-size: 0.7rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.02em;
            }

            .recording-badge.youtube {
                background: #ff000020;
                color: #cc0000;
            }

            .recording-badge.audio {
                background: #0066ff20;
                color: #0055dd;
            }

            .recording-name {
                flex: 1;
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: var(--text-primary);
            }

            .recording-syncpoints {
                color: var(--text-secondary);
                font-size: 0.8rem;
                white-space: nowrap;
            }

            .recording-primary {
                font-size: 0.7rem;
                color: var(--primary-color);
                font-weight: 600;
            }

            .resync-btn {
                margin-top: 0.5rem;
                font-size: 0.8rem;
                padding: 0.375rem 0.75rem;
            }

            .no-recordings {
                color: var(--text-secondary);
                font-size: 0.85rem;
                font-style: italic;
            }
        `,
    ];

    @property({ type: String, attribute: 'auth-token' })
    authToken = '';

    @litState()
    private _mode: 'create' | 'edit' = 'create';

    @litState()
    private _createSource: CreateSource = 'upload';

    @litState()
    private _editingSlice: SliceData | null = null;

    @litState()
    private _error = '';

    @litState()
    private _loading = false;

    @litState()
    private _recordings: RecordingInfo[] = [];

    @litState()
    private _syncLoading = false;

    @litState()
    private _audioTrack: { id: number; recordingName: string; durationMs: number } | null = null;

    @litState()
    private _audioLoading = false;

    @litState()
    private _settingPrimaryId: number | null = null;

    @litState()
    private _convertingToGp = false;

    @litState()
    private _convertSuccess = '';

    @query('modal-dialog')
    private _modal!: ModalDialog;

    @query('#slice-name')
    private _nameInput!: HTMLInputElement;

    @query('#slice-artist')
    private _artistInput!: HTMLInputElement;

    @query('#slice-difficulty')
    private _difficultySelect!: HTMLSelectElement;

    @query('#slice-tags')
    private _tagsInput!: HTMLInputElement;

    @query('#slice-musicxml')
    private _fileInput!: HTMLInputElement;

    @query('#slice-scorehash')
    private _scorehashInput!: HTMLInputElement;

    showCreate(): void {
        this._mode = 'create';
        this._createSource = 'upload';
        this._editingSlice = null;
        this._error = '';
        this._loading = false;
        this._recordings = [];
        this._syncLoading = false;
        // Reset form after render
        this.updateComplete.then(() => {
            this._resetForm();
            this._modal?.show();
        });
    }

    showEdit(slice: SliceData): void {
        this._mode = 'edit';
        this._editingSlice = slice;
        this._error = '';
        this._loading = false;
        this._recordings = [];
        this._syncLoading = false;
        this._convertingToGp = false;
        this._convertSuccess = '';
        // Populate form after render
        this.updateComplete.then(() => {
            this._populateForm(slice);
            this._modal?.show();
        });
        // Fetch recordings if this is a Soundslice slice
        if (slice.soundsliceScorehash) {
            this._fetchRecordings(slice.id);
        }
        // Always fetch audio track status (not just for Soundslice slices)
        this._fetchAudioTrack(slice.id);
    }

    hide(): void {
        this._modal?.hide();
        this._editingSlice = null;
        this._error = '';
        this._loading = false;
        this._recordings = [];
        this._syncLoading = false;
        this._audioTrack = null;
        this._audioLoading = false;
        this._convertingToGp = false;
        this._convertSuccess = '';
    }

    private _resetForm(): void {
        if (this._nameInput) this._nameInput.value = '';
        if (this._artistInput) this._artistInput.value = '';
        if (this._difficultySelect) this._difficultySelect.value = '';
        if (this._tagsInput) this._tagsInput.value = '';
        if (this._fileInput) this._fileInput.value = '';
        if (this._scorehashInput) this._scorehashInput.value = '';
    }

    private _populateForm(slice: SliceData): void {
        if (this._nameInput) this._nameInput.value = slice.name;
        if (this._artistInput) this._artistInput.value = slice.artist || '';
        if (this._difficultySelect) this._difficultySelect.value = slice.difficulty || '';
        if (this._tagsInput) this._tagsInput.value = slice.tags?.join(', ') || '';
        if (this._fileInput) this._fileInput.value = '';
    }

    private _setCreateSource(source: CreateSource): void {
        this._createSource = source;
        this._error = '';
    }

    private async _onSubmit(e: Event): Promise<void> {
        e.preventDefault();
        if (this._loading) return;

        if (this._mode === 'create' && this._createSource === 'soundslice') {
            await this._importFromSoundslice();
            return;
        }

        // Validate
        const name = this._nameInput?.value.trim();
        if (!name) {
            this._error = 'Slice name is required';
            return;
        }

        const file = this._fileInput?.files?.[0];
        if (this._mode === 'create' && !file) {
            this._error = 'Music file is required';
            return;
        }

        if (file) {
            const validExtensions = ['.musicxml', '.xml', '.mxl', '.gp'];
            const hasValidExtension = validExtensions.some(ext =>
                file.name.toLowerCase().endsWith(ext)
            );
            if (!hasValidExtension) {
                this._error = 'Invalid file type. Please upload a .musicxml, .xml, .mxl, or .gp file';
                return;
            }
        }

        // Build FormData
        const formData = new FormData();
        formData.append('name', name);

        if (this._artistInput?.value.trim()) {
            formData.append('artist', this._artistInput.value.trim());
        }
        if (this._difficultySelect?.value) {
            formData.append('difficulty', this._difficultySelect.value);
        }
        if (this._tagsInput?.value.trim()) {
            const tags = this._tagsInput.value.split(',').map(t => t.trim()).filter(t => t);
            formData.append('tags', JSON.stringify(tags));
        }
        if (file) {
            formData.append('musicxmlFile', file);
        }

        this._loading = true;
        this._error = '';

        try {
            if (this._mode === 'edit' && this._editingSlice) {
                await this._updateSlice(this._editingSlice.id, formData);
            } else {
                await this._createSlice(formData);
            }
        } finally {
            this._loading = false;
        }
    }

    private async _importFromSoundslice(): Promise<void> {
        const scorehash = this._scorehashInput?.value.trim();
        if (!scorehash) {
            this._error = 'Soundslice Slice ID is required';
            return;
        }

        this._loading = true;
        this._error = '';

        try {
            const body: Record<string, any> = { scorehash };

            // Optional overrides
            const name = this._nameInput?.value.trim();
            if (name) body.name = name;

            const artist = this._artistInput?.value.trim();
            if (artist) body.artist = artist;

            if (this._difficultySelect?.value) {
                body.difficulty = this._difficultySelect.value;
            }

            if (this._tagsInput?.value.trim()) {
                const tags = this._tagsInput.value.split(',').map(t => t.trim()).filter(t => t);
                body.tags = JSON.stringify(tags);
            }

            const response = await fetch('api/admin/slices/import-soundslice', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(body),
            });

            const result = await response.json();

            if (response.status === 409 && result.conflict) {
                this.dispatchEvent(
                    new CustomEvent('slice-conflict', {
                        bubbles: true,
                        composed: true,
                        detail: { conflictData: result },
                    })
                );
                return;
            }

            if (!response.ok) {
                this._error = result.error || 'Failed to import from Soundslice';
                return;
            }

            this.hide();
            this.dispatchEvent(
                new CustomEvent('slice-saved', {
                    bubbles: true,
                    composed: true,
                    detail: { mode: 'create' },
                })
            );
        } catch (error) {
            console.error('Soundslice import error:', error);
            this._error = 'Network error. Please try again.';
        } finally {
            this._loading = false;
        }
    }

    private async _createSlice(formData: FormData): Promise<void> {
        try {
            const response = await fetch('api/admin/slices', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${this.authToken}` },
                body: formData,
            });

            const result = await response.json();

            if (response.status === 409 && result.conflict) {
                // Emit conflict event for parent to handle
                this.dispatchEvent(
                    new CustomEvent('slice-conflict', {
                        bubbles: true,
                        composed: true,
                        detail: { conflictData: result, pendingFormData: formData },
                    })
                );
                return;
            }

            if (!response.ok) {
                this._error = result.error || 'Failed to create slice';
                return;
            }

            this.hide();
            this.dispatchEvent(
                new CustomEvent('slice-saved', {
                    bubbles: true,
                    composed: true,
                    detail: { mode: 'create' },
                })
            );
        } catch (error) {
            console.error('Create slice error:', error);
            this._error = 'Network error. Please try again.';
        }
    }

    async _updateSlice(sliceId: string, formData: FormData): Promise<void> {
        try {
            const response = await fetch(`api/admin/slices/${sliceId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${this.authToken}` },
                body: formData,
            });

            const result = await response.json();

            if (!response.ok) {
                this._error = result.error || 'Failed to update slice';
                return;
            }

            this.hide();
            this.dispatchEvent(
                new CustomEvent('slice-saved', {
                    bubbles: true,
                    composed: true,
                    detail: { mode: 'edit' },
                })
            );
        } catch (error) {
            console.error('Update slice error:', error);
            this._error = 'Network error. Please try again.';
        }
    }

    /** Re-create with skipDuplicateCheck (called by parent during conflict resolution) */
    async createWithSkipDuplicate(formData: FormData): Promise<boolean> {
        formData.append('skipDuplicateCheck', 'true');

        try {
            const response = await fetch('api/admin/slices', {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${this.authToken}` },
                body: formData,
            });

            const result = await response.json();

            if (!response.ok) {
                this._error = result.error || 'Failed to create slice';
                this._modal?.show();
                return false;
            }

            this.hide();
            this.dispatchEvent(
                new CustomEvent('slice-saved', {
                    bubbles: true,
                    composed: true,
                    detail: { mode: 'create' },
                })
            );
            return true;
        } catch (error) {
            console.error('Create slice error:', error);
            this._error = 'Network error. Please try again.';
            this._modal?.show();
            return false;
        }
    }

    /** Update existing slice (called by parent during conflict resolution) */
    async updateExisting(sliceId: string, formData: FormData): Promise<boolean> {
        try {
            const response = await fetch(`api/admin/slices/${sliceId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${this.authToken}` },
                body: formData,
            });

            const result = await response.json();

            if (!response.ok) {
                this._error = result.error || 'Failed to update slice';
                return false;
            }

            this.hide();
            this.dispatchEvent(
                new CustomEvent('slice-saved', {
                    bubbles: true,
                    composed: true,
                    detail: { mode: 'edit' },
                })
            );
            return true;
        } catch (error) {
            console.error('Update slice error:', error);
            this._error = 'Network error. Please try again.';
            return false;
        }
    }

    private async _fetchRecordings(sliceId: string): Promise<void> {
        try {
            const response = await fetch(`api/slices/${sliceId}/recordings`);
            if (response.ok) {
                this._recordings = await response.json();
            }
        } catch (err) {
            console.warn('Failed to fetch recordings:', err);
        }
    }

    private async _setPrimaryRecording(recordingId: number): Promise<void> {
        if (!this._editingSlice || this._settingPrimaryId !== null) return;

        this._settingPrimaryId = recordingId;
        try {
            const response = await fetch(
                `api/admin/slices/${this._editingSlice.id}/recordings/${recordingId}/set-primary`,
                {
                    method: 'PATCH',
                    headers: { 'Authorization': `Bearer ${this.authToken}` },
                }
            );

            if (response.ok) {
                this._recordings = await response.json();
            } else {
                const result = await response.json();
                this._error = result.error || 'Failed to set primary recording';
            }
        } catch (err) {
            console.error('Set primary error:', err);
            this._error = 'Network error while setting primary recording.';
        } finally {
            this._settingPrimaryId = null;
        }
    }

    private async _resyncRecordings(): Promise<void> {
        if (!this._editingSlice || this._syncLoading) return;

        this._syncLoading = true;
        try {
            const response = await fetch(`api/admin/slices/${this._editingSlice.id}/sync-soundslice`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                this._recordings = await response.json();
            } else {
                const result = await response.json();
                this._error = result.error || 'Failed to re-sync recordings';
            }
        } catch (err) {
            console.error('Re-sync error:', err);
            this._error = 'Network error during re-sync.';
        } finally {
            this._syncLoading = false;
        }
    }

    private async _fetchAudioTrack(sliceId: string): Promise<void> {
        try {
            const response = await fetch(`api/slices/${sliceId}/audio-track`);
            if (response.ok) {
                const data = await response.json();
                if (data.hasAudioTrack) {
                    this._audioTrack = {
                        id: data.id,
                        recordingName: data.recordingName || 'audio.mp3',
                        durationMs: data.durationMs || 0
                    };
                } else {
                    this._audioTrack = null;
                }
            }
        } catch (err) {
            console.warn('Failed to fetch audio track:', err);
        }
    }

    /** Badge text for an audio track, derived from its filename extension. */
    private _audioFormatLabel(recordingName: string): string {
        const match = /\.([a-z0-9]+)$/i.exec(recordingName);
        return match ? match[1].toUpperCase() : 'AUDIO';
    }

    private _onUploadAudioClick(): void {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.mp3,.wav';
        input.addEventListener('change', async () => {
            const file = input.files?.[0];
            if (!file || !this._editingSlice) return;
            await this._uploadAudioTrack(file, this._editingSlice.id);
        });
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    }

    private async _uploadAudioTrack(file: File, sliceId: string): Promise<void> {
        this._audioLoading = true;
        try {
            const formData = new FormData();
            formData.append('audioFile', file);
            const response = await fetch(`api/admin/slices/${sliceId}/audio-track`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                },
                body: formData,
            });
            if (response.ok) {
                const data = await response.json();
                this._audioTrack = {
                    id: data.id,
                    recordingName: data.recordingName || file.name,
                    durationMs: data.durationMs || 0,
                };
            } else {
                const result = await response.json();
                this._error = result.error || 'Failed to upload audio track';
            }
        } catch (err) {
            console.error('Audio upload error:', err);
            this._error = 'Network error uploading audio.';
        } finally {
            this._audioLoading = false;
        }
    }

    private async _onDownloadGpClick(): Promise<void> {
        if (!this._editingSlice) return;
        this._audioLoading = true;
        try {
            const response = await fetch(`api/admin/slices/${this._editingSlice.id}/download-gp`, {
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                },
            });
            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${this._editingSlice.name || 'Untitled'}.gp`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } else {
                const result = await response.json();
                this._error = result.error || 'Failed to download GP file';
            }
        } catch (err) {
            console.error('GP download error:', err);
            this._error = 'Network error downloading GP file.';
        } finally {
            this._audioLoading = false;
        }
    }

    private _onUploadGpClick(): void {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.gp';
        input.addEventListener('change', async () => {
            const file = input.files?.[0];
            if (!file || !this._editingSlice) return;
            await this._uploadGpTrack(file, this._editingSlice.id);
        });
        document.body.appendChild(input);
        input.click();
        document.body.removeChild(input);
    }

    private async _uploadGpTrack(file: File, sliceId: string): Promise<void> {
        this._audioLoading = true;
        try {
            const formData = new FormData();
            formData.append('gpFile', file);
            const response = await fetch(`api/admin/slices/${sliceId}/audio-track-gp`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                },
                body: formData,
            });
            if (response.ok) {
                const data = await response.json();
                this._audioTrack = {
                    id: data.id,
                    recordingName: data.recordingName || file.name,
                    durationMs: data.durationMs || 0,
                };
            } else {
                const result = await response.json();
                this._error = result.error || 'Failed to upload GP file';
            }
        } catch (err) {
            console.error('GP upload error:', err);
            this._error = 'Network error uploading GP file.';
        } finally {
            this._audioLoading = false;
        }
    }

    private async _removeAudioTrack(): Promise<void> {
        if (!this._editingSlice) return;
        this._audioLoading = true;
        try {
            const response = await fetch(`api/admin/slices/${this._editingSlice.id}/audio-track`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                this._audioTrack = null;
            } else {
                const result = await response.json();
                this._error = result.error || 'Failed to remove audio track';
            }
        } catch (err) {
            console.error('Audio remove error:', err);
            this._error = 'Network error removing audio.';
        } finally {
            this._audioLoading = false;
        }
    }

    private async _onConvertToGpClick(): Promise<void> {
        if (!this._editingSlice || this._convertingToGp) return;
        this._convertingToGp = true;
        this._error = '';
        this._convertSuccess = '';
        try {
            const response = await fetch(`api/admin/slices/${this._editingSlice.id}/convert-to-gp`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${this.authToken}`,
                    'Content-Type': 'application/json',
                },
            });
            const result = await response.json();
            if (!response.ok) {
                if (response.status === 422) {
                    this._error = `Conversion produced a structurally different score — sync points may not align. Aborted. (${result.error || ''})`;
                } else {
                    this._error = result.error || 'Failed to convert slice to GP';
                }
                return;
            }
            if (result.slice) {
                this._editingSlice = { ...this._editingSlice, ...result.slice };
            }
            this._convertSuccess = 'Converted to GP. Original MusicXML kept on the server.';
            this.dispatchEvent(new CustomEvent('slicesUpdated', { bubbles: true, composed: true }));
        } catch (err) {
            console.error('Convert to GP error:', err);
            this._error = 'Network error during conversion.';
        } finally {
            this._convertingToGp = false;
        }
    }

    private _onCancel(): void {
        this.hide();
    }

    private _onModalClose(): void {
        this._editingSlice = null;
        this._error = '';
        this._loading = false;
    }

    private get _heading(): string {
        if (this._mode === 'edit') return 'Edit Slice';
        if (this._createSource === 'soundslice') return 'Import from Soundslice';
        return 'Create New Slice';
    }

    private get _submitText(): string {
        if (this._loading) {
            if (this._mode === 'edit') return 'Updating...';
            if (this._createSource === 'soundslice') return 'Importing...';
            return 'Creating...';
        }
        if (this._mode === 'edit') return 'Update Slice';
        if (this._createSource === 'soundslice') return 'Import Slice';
        return 'Create Slice';
    }

    private get _currentFileName(): string | null {
        if (this._mode === 'edit' && this._editingSlice?.metadata?.musicxml) {
            return this._editingSlice.metadata.musicxml.originalName;
        }
        return null;
    }

    private _renderSourceToggle() {
        if (this._mode !== 'create') return nothing;

        return html`
            <div class="source-toggle">
                <button
                    type="button"
                    class=${this._createSource === 'upload' ? 'active' : ''}
                    @click=${() => this._setCreateSource('upload')}
                >
                    Upload File
                </button>
                <button
                    type="button"
                    class=${this._createSource === 'soundslice' ? 'active' : ''}
                    @click=${() => this._setCreateSource('soundslice')}
                >
                    Import from Soundslice
                </button>
            </div>
        `;
    }

    private _renderConvertToGpSection() {
        if (this._mode !== 'edit' || !this._editingSlice) return nothing;
        const filename = this._currentFileName || '';
        const lower = filename.toLowerCase();
        const isConvertible = lower.endsWith('.musicxml') || lower.endsWith('.xml') || lower.endsWith('.mxl');

        return html`
            <div class="recordings-section">
                <h4>Score Format</h4>
                <button
                    type="button"
                    class="btn btn-secondary resync-btn"
                    ?disabled=${this._convertingToGp || !isConvertible}
                    @click=${this._onConvertToGpClick}
                >
                    ${this._convertingToGp ? 'Converting...' : 'Convert MusicXML → GP (fix percussion playback)'}
                </button>
                <p class="import-hint">
                    ${isConvertible
                        ? 'Use this if drum notes play at the wrong pitch when they overlap. The original MusicXML is preserved on the server.'
                        : 'Already in Guitar Pro format. No conversion needed.'}
                </p>
                ${this._convertSuccess
                    ? html`<p class="form-hint" style="color: var(--success-color, #059669);">${this._convertSuccess}</p>`
                    : nothing}
            </div>
        `;
    }

    private _renderRecordingsSection() {
        if (this._mode !== 'edit' || !this._editingSlice?.soundsliceScorehash) {
            return nothing;
        }

        return html`
            <div class="recordings-section">
                <h4>Recordings</h4>
                ${this._recordings.length > 0
                    ? this._recordings.map(rec => {
                        const isYoutube = rec.recordingType === 'youtube';
                        const badgeClass = isYoutube ? 'youtube' : 'audio';
                        const badgeLabel = isYoutube ? 'YouTube' : 'Audio';
                        return html`
                            <div class="recording-item">
                                <span class="recording-badge ${badgeClass}">${badgeLabel}</span>
                                <span class="recording-name">${rec.recordingName || 'Untitled'}</span>
                                <span class="recording-syncpoints">${rec.syncpointCount} syncpoints</span>
                                ${rec.isPrimary
                                    ? html`<span class="recording-primary">PRIMARY</span>`
                                    : html`<button
                                        type="button"
                                        class="btn btn-secondary resync-btn"
                                        ?disabled=${this._settingPrimaryId !== null}
                                        @click=${() => this._setPrimaryRecording(rec.id)}
                                    >Set as Primary</button>`}
                            </div>
                        `;
                    })
                    : html`<p class="no-recordings">No recordings found</p>`
                }
                <button
                    type="button"
                    class="btn btn-secondary resync-btn"
                    ?disabled=${this._syncLoading}
                    @click=${this._resyncRecordings}
                >
                    ${this._syncLoading ? 'Re-syncing...' : 'Re-sync from Soundslice'}
                </button>
            </div>
        `;
    }

    private _renderAudioTrackSection() {
        if (this._mode !== 'edit') return nothing;

        const durationStr = this._audioTrack?.durationMs
            ? `${Math.floor(this._audioTrack.durationMs / 1000)}s`
            : '';

        return html`
            <div class="recordings-section">
                <h4>Audio Track</h4>
                ${this._audioTrack
                    ? html`
                        <div class="recording-item">
                            <span class="recording-badge audio">${this._audioFormatLabel(this._audioTrack.recordingName)}</span>
                            <span class="recording-name">${this._audioTrack.recordingName}</span>
                            ${durationStr ? html`<span class="recording-syncpoints">${durationStr}</span>` : nothing}
                        </div>`
                    : html`<p class="no-recordings">None</p>`
                }
                <div style="display: flex; gap: 0.5rem; margin-top: 0.5rem;">
                    <button type="button" class="btn btn-secondary resync-btn"
                        ?disabled=${this._audioLoading}
                        @click=${this._onUploadAudioClick}>
                        ${this._audioLoading ? 'Uploading...' : 'Upload Audio'}
                    </button>
                    <button type="button" class="btn btn-secondary resync-btn"
                        ?disabled=${this._audioLoading}
                        @click=${this._onUploadGpClick}>
                        ${this._audioLoading ? 'Uploading...' : 'Upload GP'}
                    </button>
                    ${this._audioTrack ? html`
                        <button type="button" class="btn btn-secondary resync-btn"
                            ?disabled=${this._audioLoading}
                            @click=${this._onDownloadGpClick}>
                            ${this._audioLoading ? 'Downloading...' : 'Download GP'}
                        </button>
                        <button type="button" class="btn btn-secondary resync-btn"
                            ?disabled=${this._audioLoading}
                            @click=${this._removeAudioTrack}>
                            Remove
                        </button>` : nothing}
                </div>
            </div>
        `;
    }

    private _renderFileOrSoundsliceField() {
        if (this._mode === 'create' && this._createSource === 'soundslice') {
            return html`
                <div class="form-group">
                    <label for="slice-scorehash">Soundslice Slice ID *</label>
                    <input
                        type="text"
                        id="slice-scorehash"
                        name="scorehash"
                        required
                        placeholder="e.g., abc123"
                    >
                    <p class="import-hint">
                        The slice ID is the code in the Soundslice URL after /slices/.
                        Name and artist will be auto-detected if left blank.
                    </p>
                </div>
            `;
        }

        return html`
            <div class="form-group">
                <label for="slice-musicxml">
                    Music File (MusicXML / GuitarPro)${this._mode === 'create' ? ' *' : ''}
                </label>
                <input
                    type="file"
                    id="slice-musicxml"
                    name="musicxmlFile"
                    accept=".musicxml,.xml,.mxl,.gp"
                    ?required=${this._mode === 'create'}
                >
                ${this._currentFileName
                    ? html`<p class="form-hint">Current file: ${this._currentFileName}</p>`
                    : nothing}
            </div>
        `;
    }

    render() {
        const isSoundsliceMode = this._mode === 'create' && this._createSource === 'soundslice';
        const nameRequired = !isSoundsliceMode;

        return html`
            <modal-dialog
                .heading=${this._heading}
                size="medium"
                @modal-close=${this._onModalClose}
            >
                ${this._loading && isSoundsliceMode
                    ? html`
                        <div class="loading-message">
                            Importing from Soundslice... This may take 15-30 seconds.
                        </div>
                    `
                    : nothing}

                <form @submit=${this._onSubmit} style=${this._loading && isSoundsliceMode ? 'display: none' : ''}>
                    ${this._renderSourceToggle()}

                    <div class="form-group">
                        <label for="slice-name">
                            Slice Name${nameRequired ? ' *' : ''}
                        </label>
                        <input
                            type="text"
                            id="slice-name"
                            name="name"
                            ?required=${nameRequired}
                            maxlength="200"
                            placeholder=${isSoundsliceMode
                                ? 'Leave blank to auto-detect from Soundslice'
                                : 'e.g., Happy Birthday - Beginner'}
                        >
                    </div>

                    <div class="form-group">
                        <label for="slice-artist">Artist / Composer</label>
                        <input
                            type="text"
                            id="slice-artist"
                            name="artist"
                            maxlength="100"
                            placeholder=${isSoundsliceMode
                                ? 'Leave blank to auto-detect'
                                : 'e.g., Traditional'}
                        >
                    </div>

                    <div class="form-group">
                        <label for="slice-difficulty">Difficulty</label>
                        <select id="slice-difficulty" name="difficulty">
                            <option value="">-- Select --</option>
                            <option value="Beginner">Beginner</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Advanced">Advanced</option>
                        </select>
                    </div>

                    <div class="form-group">
                        <label for="slice-tags">Tags (comma-separated)</label>
                        <input
                            type="text"
                            id="slice-tags"
                            name="tags"
                            placeholder="e.g., classical, beginner-friendly"
                        >
                    </div>

                    ${this._renderFileOrSoundsliceField()}

                    ${this._renderConvertToGpSection()}

                    ${this._renderRecordingsSection()}

                    ${this._renderAudioTrackSection()}

                    ${this._error
                        ? html`<p class="error-message">${this._error}</p>`
                        : nothing}

                    <div class="form-actions">
                        <button
                            type="submit"
                            class="btn btn-primary"
                            ?disabled=${this._loading}
                        >
                            ${this._submitText}
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary"
                            @click=${this._onCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </modal-dialog>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'slice-form-modal': SliceFormModal;
    }
}
